<template>
  <div class="webhooks-settings-container mt-8 pt-8 border-t border-gray-200 dark:border-zinc-700">
    <h2 class="text-xl font-semibold mb-3">Webhooks</h2>
    <p class="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
      Extend your content's capabilities with Zight's webhooks. Send your data to other platforms, set up workflows and automations that help you save time.
      <br /><br />
      Ready to setup your automation? Read our
      <a href="https://support.zight.com/hc/en-us/articles/31446701947031" target="_blank" class="text-blue-500 hover:underline">Webhooks documentation</a>.
    </p>
    <div class="zt-settings-page-semismall">
      <FormKit type="form" @submit="handleUpdateWebhook" :actions="false" v-slot="{ state }">
        <FormKit
          type="url"
          name="webhook_url"
          label="Webhook URL"
          placeholder="https://your-webhook-url-here.com/notify"
          v-model="webhookUrl"
          :validation="[['url'], [isValidUrlRule, 'Please enter a valid URL (e.g., https://example.com/webhook)']]"
          validation-visibility="live"
        />
        <div class="mt-4">
          <Button submit variant="blue" :is-loading="loading" :disabled="!state.valid" data-testid="update-webhook-button"> Update Webhooks </Button>
        </div>
      </FormKit>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch } from 'vue';
import { Button } from '@/storybook';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { storeToRefs } from 'pinia';
import { FormKit } from '@formkit/vue';
import { useToast } from '@/composables/useToast';
import { useUrlValidation } from '@/composables/useUrlValidation';

const organizationStore = useOrganizationStore();
const { preferences } = storeToRefs(organizationStore);
const { success, error } = useToast();
const { isValidUrl, isValidUrlRule } = useUrlValidation();

const webhookUrl = ref('');
const loading = ref(false);

onMounted(() => {
  if (preferences.value?.webhookUrl) {
    webhookUrl.value = preferences.value.webhookUrl;
  }
});

watch(
  () => preferences.value?.webhookUrl,
  newUrl => {
    if (newUrl !== undefined) {
      webhookUrl.value = newUrl || '';
    }
  },
  { immediate: true }
);

const handleUpdateWebhook = async (data: any) => {
  // Prevent multiple simultaneous requests
  if (loading.value) {
    return;
  }

  // Extract the webhook URL from form data
  const url = data.webhook_url || webhookUrl.value;

  // Additional validation (FormKit should handle this now, but keeping as backup)
  if (url && !isValidUrl(url)) {
    error('Please enter a valid URL (e.g., https://example.com/webhook)', {
      title: 'Invalid URL Format',
    });
    return;
  }

  loading.value = true;
  try {
    await organizationStore.updateWebhookUrl(url);

    // Show success message
    success('Webhook URL has been updated successfully.', {
      title: 'Webhooks Updated',
    });
  } catch (err) {
    console.error('Failed to update webhook URL:', err);

    // Show user-friendly error message
    const errorMessage = err?.response?.data?.message || err?.message || 'An unexpected error occurred while updating the webhook URL.';
    error(errorMessage, {
      title: 'Update Failed',
    });
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.zt-settings-page-semismall {
  max-width: 600px;
}
</style>
